import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import NoInternetScreen from '../NoInternetScreen';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../store/slices/authSlice';
import './MainLayout.css';
import {
  BsList,
  BsSpeedometer2,
  BsCameraVideo,
  BsFileText,
  BsCalendarEvent,
  BsGlobe2,
  BsBroadcast,
  BsFileEarmarkRichtext,
  BsTags,
  BsImages,
  BsPeopleFill,
  BsBoxArrowRight,
  BsEnvelope,
  BsBuilding,
} from 'react-icons/bs';
import listImage from '../../assets/Frame.png'
import { AiOutlineAppstore } from 'react-icons/ai';
/* ── Nav groups ─────────────────────────────────────────────────────────── */
const NAV_GROUPS = [
  {
    label: 'PRINCIPAL',
    items: [
      { to: '/dashboard', icon: <BsSpeedometer2 size={17} />, label: 'Tableau de bord', exact: true },
    ],
  },
  {
    label: 'CONTENU',
    items: [
      { to: '/videos', icon: <BsCameraVideo size={17} />, label: 'Vidéos' },
      { to: '/broadcasts', icon: <BsBroadcast size={17} />, label: 'Diffusions en direct' },
      // { to: '/parametres',      icon: <BsFileText size={17} />,            label: 'Paramètres généraux' },
      { to: '/affiches', icon: <BsCalendarEvent size={17} />, label: 'Affiches' },
      { to: '/pages', icon: <BsFileEarmarkRichtext size={17} />, label: 'Pages du site' },
    ],
  },
  {
    label: 'ORGANISATION',
    items: [
      { to: '/representations', icon: <BsGlobe2 size={17} />, label: 'Représentations' },
      { to: '/prayer-centers', icon: <BsBuilding size={17} />, label: 'Centres de prière' },
      { to: '/canaux', icon: <BsBroadcast size={17} />, label: 'Canaux de communication' },
      { to: '/categories', icon: <BsTags size={17} />, label: 'Catégories' },
      { to: '/media', icon: <BsImages size={17} />, label: 'Médiathèque' },
    ],
  },
];

const ADMIN_GROUP = {
  label: 'ADMINISTRATION',
  items: [
    { to: '/users', icon: <BsPeopleFill size={17} />, label: 'Utilisateurs' },
    { to: '/contact-messages', icon: <BsEnvelope size={17} />, label: 'Messages de contact' },
  ],
};

const isMobile = () => window.innerWidth < 768;

/* ── NavItem ─────────────────────────────────────────────────────────────── */
function NavItem({ to, icon, label, exact, collapsed }) {
  return (
    <NavLink
      to={to}
      end={exact}
      className={`sidebar-nav-link${collapsed ? ' sidebar-collapsed' : ''}`}
      style={({ isActive }) =>
        collapsed
          ? {
            color: isActive ? '#fff' : '#555',
            backgroundColor: isActive ? '#8B0000' : 'transparent',
            fontWeight: isActive ? 600 : 400,
          }
          : {
            color: isActive ? '#fff' : '#555',
            backgroundColor: isActive ? '#8B0000' : 'transparent',
            fontWeight: isActive ? 600 : 400,
            borderLeft: isActive ? '4px solid #e8a0a0' : '4px solid transparent',
            boxSizing: 'border-box',
          }
      }
    >
      <span className="sidebar-icon-wrap" style={{ flexShrink: 0, display: 'flex', opacity: 0.9 }}>{icon}</span>
      {!collapsed && (
        <span className="sidebar-label" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
        </span>
      )}
      {collapsed && <span className="sidebar-tooltip">{label}</span>}
    </NavLink>
  );
}

/* ── Main layout ─────────────────────────────────────────────────────────── */
export default function MainLayout() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector(state => state.auth.user);
  const isSuperAdmin = user?.role === 'superadmin';

  const [sidebarOpen, setSidebarOpen] = useState(!isMobile());
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobile, setMobile] = useState(isMobile());
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const fabRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!mobile || !sidebarOpen) return undefined;
    const handleClickOutside = (event) => {
      if (fabRef.current?.contains(event.target)) return;
      if (panelRef.current?.contains(event.target)) return;
      setSidebarOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobile, sidebarOpen]);

  useEffect(() => {
    const onResize = () => {
      const m = isMobile();
      setMobile(m);
      if (m) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  useEffect(() => {
    if (mobile) setSidebarOpen(false);
  }, [location.pathname, mobile]);

  const handleLogout = async () => {
    await dispatch(logout());
    navigate('/login');
  };

  const toggleSidebar = () => {
    if (mobile) {
      setSidebarOpen(open => !open);
    } else {
      setSidebarCollapsed(collapsed => !collapsed);
    }
  };

  const groups = isSuperAdmin ? [...NAV_GROUPS, ADMIN_GROUP] : NAV_GROUPS;
  // const groups = isSuperAdmin ? [...NAV_GROUPS] : NAV_GROUPS;

  return (
    <div style={{ height: '100dvh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

      {/* ── Top bar ────────────────────────────────────────────────────── */}
      <header style={{
        backgroundColor: '#8B0000', height: '90px',
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 16px', flexShrink: 0, zIndex: 400,
      }}>
        {/* Hamburger */}
        <button onClick={toggleSidebar}
          style={{ background: 'none', border: 'none', cursor: 'pointer', display: mobile ? 'none' : 'flex', alignItems: 'center', padding: 4 }}>
          <BsList size={26} color="#fff" />
        </button>

        {/* Brand */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src="/logo192.png" alt="logo" style={{ height: '70px', width: '57px', borderRadius: '50%' }} />
          <span style={{ color: '#fff', fontFamily: 'serif', letterSpacing: 1, fontSize: 'clamp(11px,2.2vw,17px)' }}>
            MINISTÈRE CATHOLIQUE MISERICORDE
          </span>
        </div>

        {/* User + logout */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={handleLogout}
            title="Déconnexion"
            style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, color: '#fff', fontSize: 12 }}>
            <BsBoxArrowRight size={20} color="#fff" />

            {!mobile &&
              <div className='d-flex flex-column gap-1 ms-2 align-items-start'>
                {user && (
                  <span className='text-capitalize' style={{ color: '#fce8e8', display: mobile ? 'none' : 'block' }}>
                    {user.username}{isSuperAdmin ? ' ★' : ''}
                  </span>
                )}
                <span style={{ letterSpacing: 1 }}>DÉCONNEXION</span>
              </div>}
          </button>
        </div>
      </header>

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* ── Sidebar ────────────────────────────────────────────────── */}
        {!mobile && (
          <aside
            className="hide-scrollbar"
            style={{
              width: sidebarCollapsed ? 64 : 230,
              minWidth: sidebarCollapsed ? 64 : 230,
              position: 'relative',
              backgroundColor: '#fff',
              borderRight: '1px solid #ebebeb',
              flexShrink: 0,
              zIndex: 300,
              overflowY: sidebarCollapsed ? 'visible' : 'auto',
              overflowX: sidebarCollapsed ? 'visible' : 'hidden',
              transition: 'width 0.2s ease',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
            }}
          >
            {/* Nav groups */}
            {groups.map(group => (
              <div key={group.label}>
                {!sidebarCollapsed && (
                  <div style={{
                    padding: '10px 20px 4px',
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: 1.5,
                    color: '#aaa',
                    textTransform: 'uppercase',
                  }}>
                    {group.label}
                  </div>
                )}
                {group.items.map(item => (
                  <NavItem key={item.to} {...item} collapsed={sidebarCollapsed} />
                ))}
              </div>
            ))}

            {/* Bottom spacer */}
            <div style={{ flex: 1 }} />

            {/* Version */}
            {!sidebarCollapsed && (
              <div style={{ padding: '10px 20px', fontSize: 10, color: '#ccc', borderTop: '1px solid #f5f5f5' }}>
                MCM Admin v2.0
              </div>
            )}
          </aside>
        )}

        {/* Mobile FAB menu */}
        {mobile && (
          <>
            <button ref={fabRef} className="fab-menu-btn" onClick={() => setSidebarOpen(open => !open)}>
              {/* <BsList size={22} /> */}
              {sidebarOpen ?
                <img src={listImage} width={25} height={25} alt='icon' />
                :
                <AiOutlineAppstore size={30} color='white' />
              }
            </button>
            {sidebarOpen && (
              <div className="fab-menu-backdrop" onClick={() => setSidebarOpen(false)} />
            )}
            {sidebarOpen && (
              <div ref={panelRef} className="fab-menu-panel">
                {groups.map(group => (
                  <div key={group.label}>
                    <div className="fab-menu-group-label">{group.label}</div>
                    {group.items.map(item => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.exact}
                        className={({ isActive }) => isActive ? 'active' : ''}
                        onClick={() => setSidebarOpen(false)}
                      >
                        {item.label}
                      </NavLink>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── Main content ───────────────────────────────────────────── */}
        <main
          className="hide-scrollbar"
          style={{
            flex: 1,
            padding: mobile ? 12 : 24,
            backgroundColor: '#faf9f7',
            overflowY: 'auto',
            overflowX: 'hidden',
            minWidth: 0,
          }}
        >
          {isOnline ? <Outlet /> : <NoInternetScreen />}
        </main>

      </div>
    </div>
  );
}
